import { useTranslation } from "react-i18next";
import { usePersonDetailsModal, ViewKey } from "./usePersonDetailsModal";
import { Icon, Modal, SegmentGroup, Spinner } from "@ttc3k/trekker";
import { SetStateAction, Dispatch } from "react";
import { Box, Flex } from "styled-system/jsx";
import {
  EmptyState,
  PeopleTagsManageModal,
  ViewBroadcastModal,
} from "shared/components";
import { CreateManualOrderModal } from "apps/storefront/views/CalendarView/components";
import { css } from "styled-system/css";
import { Xmark } from "iconoir-react";
import { PersonHeaderCard } from "./components/PersonHeaderCard/PersonHeaderCard";
import { LeadModal, PersonContactInfoModal } from "./modals";
import { SurveyResponseViewer } from "shared/components/organisms/SurveyResponseModal";
import { PersonDetailsInfo } from "./views/PersonDetailsInfo";
import { Timeline } from "./views/Timeline";
import { useLoggedInAs } from "shared/hooks";
import { StorefrontInfo } from "./components";
import { EnumEntityReferenceType, EnumOwnerType } from "gql/generated";
import { useStorefrontView } from "apps/storefront/StorefrontViewContext";
import { NotesContextProvider } from "shared/contextProviders";
import { createMultilingual } from "shared/utils";
import { EventCreateEditModal } from "../EventCreationAndEditing/EventCreateEditModal";

type PersonDetailsModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  personId: string | null;
  setPersonId: Dispatch<SetStateAction<string | null>>;
  portal?: boolean;
  /** When the person is removed (danger zone), run after close — e.g. refresh the people directory list. */
  onPersonDeleted?: () => void;
  adminSectionEnabled?: boolean;
};

export const PersonDetailsModal = ({
  isOpen,
  setIsOpen,
  personId,
  setPersonId,
  portal = false,
  onPersonDeleted,
  adminSectionEnabled = false,
}: PersonDetailsModalProps) => {
  const { t } = useTranslation("adminland", { keyPrefix: "MODAL" });
  const loggedInAs = useLoggedInAs();
  const { storefrontId: contextStorefrontId } = useStorefrontView();

  const {
    person,
    loading,
    error,
    storefrontId,
    isManageTagsModalOpen,
    setIsManageTagsModalOpen,
    isManualOrderModalOpen,
    setIsManualOrderModalOpen,
    isContactInfoModalOpen,
    setIsContactInfoModalOpen,
    selectedLeadId,
    setSelectedLeadId,
    refetchPerson,
    views,
    selectedView,
    setSelectedView,
    isEventCreateEditModalOpen,
    selectedEventId,
    isBroadcastModalOpen,
    selectedBroadcast,
    loadingTimelineItemId,
    onOpenEventModal,
    onCloseEventModal,
    onOpenBroadcastModal,
    onCloseBroadcastModal,
    initialResourceIdForBroadcast,
    onOpenBroadcastFromLead,
    selectedSurveyResponseId,
    setSelectedSurveyResponseId,
    onOpenFormResponseModal,
  } = usePersonDetailsModal({
    personId,
  });

  const getViewContent = () => {
    switch (selectedView) {
      case ViewKey.INFO:
        return (
          <PersonDetailsInfo
            person={person}
            adminSectionEnabled={adminSectionEnabled}
            onDeleted={() => {
              handleCloseChange({ open: false });
              onPersonDeleted?.();
            }}
          />
        );
      case ViewKey.TIMELINE:
        return (
          <Timeline
            person={person}
            onOpenLeadModal={(leadId: string) => {
              setSelectedLeadId(leadId);
              setSelectedSurveyResponseId(null);
            }}
            onOpenBroadcastModal={onOpenBroadcastModal}
            onOpenFormResponseModal={onOpenFormResponseModal}
            loadingTimelineItemId={loadingTimelineItemId}
            onOpenEventModal={onOpenEventModal}
          />
        );
    }
  };

  const handleCloseChange = ({ open }: { open: boolean }) => {
    if (!open) {
      setIsOpen(false);
      // This resets in a timeout so that the current values display while
      // the modal animates closed
      setTimeout(() => {
        setPersonId(null);
        setSelectedView(ViewKey.INFO);
      }, 300);
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        onOpenChange={handleCloseChange}
        zIndex={portal ? undefined : "medium"}
        portal={portal}
        closeTriggerProps={{
          className: css({
            h: "24px",
            w: "24px",
            backdropFilter: "blur(4px)",
            bgColor: "bg.accent.blue.dark",
            borderRadius: "full",
            p: "4px",
            color: "white",
            _hover: {
              bgColor: "bg.accent.blue.darkest",
            },
            _focusVisible: {
              bgColor: "bg.accent.blue.darkest",
            },
          }),
          style: {
            transition: "background-color 0.2s ease-in-out",
          },
          children: (
            <Icon Element={Xmark} w="16px" h="16px" color="icon.inverse" />
          ),
        }}
      >
        <NotesContextProvider
          allowArchiving={false}
          hideDisplayDate={true}
          waitForDefaultEntity={true}
          defaultEntityId={person?._id}
          defaultEntityType={EnumEntityReferenceType.Person}
          defaultEntityName={createMultilingual(
            person?.personInfo?.firstName + " " + person?.personInfo?.lastName,
          )}
        >
          <Box position="relative">
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              w="full"
              h="200px"
              bgColor="bg.accent.blue.mid"
            ></Box>
            <Flex
              direction="column"
              gap="500"
              p="500"
              pt="96px"
              position="relative"
              zIndex="1" // To ensure this content sits above the blue background box
            >
              {loading ? (
                <Flex justify="center" p="600">
                  <Spinner width="40px" height="40px" />
                </Flex>
              ) : error ? (
                <EmptyState text={t("ERROR")} p="500" h="full" />
              ) : (
                <>
                  {/* The new person header card */}
                  <PersonHeaderCard
                    person={person}
                    onContactInfoClick={() => setIsContactInfoModalOpen(true)}
                    onTagsManageClick={() => setIsManageTagsModalOpen(true)}
                  />

                  {(loggedInAs?.type === EnumOwnerType.Storefront ||
                    !!contextStorefrontId) && (
                    <StorefrontInfo
                      person={person}
                      setIsManualOrderModalOpen={setIsManualOrderModalOpen}
                    />
                  )}

                  <SegmentGroup.Root
                    segmentStyle="buttons"
                    orientation="horizontal"
                    value={selectedView}
                    onValueChange={value =>
                      setSelectedView(value.value as ViewKey)
                    }
                  >
                    {views.map(view => (
                      <SegmentGroup.Item key={view.value} value={view.value}>
                        {view.label}
                      </SegmentGroup.Item>
                    ))}
                  </SegmentGroup.Root>

                  {getViewContent()}
                </>
              )}
            </Flex>
          </Box>
        </NotesContextProvider>
      </Modal>
      <CreateManualOrderModal
        isOpen={isManualOrderModalOpen}
        onClose={(reloadOrders?: boolean) => {
          setIsManualOrderModalOpen(false);
          if (reloadOrders) {
            refetchPerson();
          }
        }}
        storefrontId={storefrontId}
        firstName={person?.personInfo?.firstName}
        lastName={person?.personInfo?.lastName}
        email={person?.personInfo?.email}
      />
      <LeadModal
        isOpen={!!selectedLeadId}
        onClose={() => {
          setSelectedLeadId(null);
        }}
        leadId={selectedLeadId}
        onOpenBroadcastFromLead={onOpenBroadcastFromLead}
        portal
      />
      <SurveyResponseViewer
        isOpen={!!selectedSurveyResponseId}
        onClose={() => {
          setSelectedSurveyResponseId(null);
        }}
        surveyResponseId={selectedSurveyResponseId}
        portal
      />
      <PersonContactInfoModal
        isOpen={isContactInfoModalOpen}
        onClose={() => setIsContactInfoModalOpen(false)}
        person={person}
      />
      <PeopleTagsManageModal
        isOpen={isManageTagsModalOpen}
        onClose={() => setIsManageTagsModalOpen(false)}
      />
      <ViewBroadcastModal
        isOpen={isBroadcastModalOpen}
        onClose={onCloseBroadcastModal}
        broadcast={selectedBroadcast}
        initialResourceId={initialResourceIdForBroadcast}
        isPreview
        displayDeletedErrorInPreview
      />
      <EventCreateEditModal
        isOpen={isEventCreateEditModalOpen}
        userEmailAddress={""}
        userFirstName={""}
        userLastName={""}
        userType={"PUBLIC"}
        initialMode={"view"}
        defaultOrganizationId={loggedInAs?.entityId ?? ""}
        defaultOperatorEntityIds={[]}
        defaultOrganizationIdsForOperatorUser={[]}
        eventId={selectedEventId}
        onClose={onCloseEventModal}
        onEventUpdated={() => {}}
        onEventDeleted={() => {}}
        hideMenuButtons={true}
      />
    </>
  );
};
